import { gql, type TypedDocumentNode } from "@apollo/client";
import type { PaginationVariables } from "@/components/Pagination/Pagination";
import type { CommissionLineFundingStatus, CommissionLineNode } from "@/pages/billing/finance/commission/lines/types";
import type { CommissionPartnerNode, CommissionPartnerStatus } from "./types";

export interface CommissionPartnersQueryResponse {
  commissionPartners: {
    nodes: CommissionPartnerNode[];
    totalCount: number;
    pageInfo: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      startCursor?: string;
      endCursor?: string;
    };
  };
}

export interface CommissionPartnersQueryVariables extends PaginationVariables {
  activeCampaignOnly?: boolean;
  search?: string;
  status?: CommissionPartnerStatus;
}

export interface CommissionPartnerQueryResponse {
  commissionPartner: CommissionPartnerNode | null;
}

export const COMMISSION_PARTNER_QUERY: TypedDocumentNode<CommissionPartnerQueryResponse, { id: string }> = gql`
  query CommissionPartner($id: ID!) {
    commissionPartner(id: $id) {
      id
      name
      legacyId
      status
      campaigns(first: 20) {
        totalCount
        nodes {
          id
          editionYear
          status
          startsAt
          endsAt
          program {
            id
            name
          }
        }
      }
    }
  }
`;

export const COMMISSION_PARTNERS_QUERY: TypedDocumentNode<
  CommissionPartnersQueryResponse,
  CommissionPartnersQueryVariables
> = gql`
  query CommissionPartners(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $activeCampaignOnly: Boolean
    $search: String
    $status: BackofficeCommissionPartnerStatus
  ) {
    commissionPartners(
      first: $first
      after: $after
      last: $last
      before: $before
      activeCampaignOnly: $activeCampaignOnly
      search: $search
      status: $status
    ) {
      nodes {
        id
        name
        legacyId
        status
        campaigns(first: 10) {
          totalCount
          nodes {
            id
            editionYear
            status
            startsAt
            endsAt
            program {
              id
              name
            }
          }
        }
      }
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

export interface CommissionPartnerLinesQueryResponse {
  commissionLines: {
    nodes: CommissionLineNode[];
    totalCount: number;
    pageInfo: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      startCursor?: string;
      endCursor?: string;
    };
  };
}

export interface CommissionPartnerLinesQueryVariables extends PaginationVariables {
  partnerId: string;
  fundingStatus?: CommissionLineFundingStatus;
  campaignId?: string;
  fundingBatchId?: string;
}

export const COMMISSION_PARTNER_LINES_QUERY: TypedDocumentNode<
  CommissionPartnerLinesQueryResponse,
  CommissionPartnerLinesQueryVariables
> = gql`
  query CommissionPartnerLines(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $partnerId: ID
    $fundingStatus: BackofficeCommissionLineFundingStatus
    $campaignId: ID
    $fundingBatchId: ID
  ) {
    commissionLines(
      first: $first
      after: $after
      last: $last
      before: $before
      partnerId: $partnerId
      fundingStatus: $fundingStatus
      campaignId: $campaignId
      fundingBatchId: $fundingBatchId
    ) {
      nodes {
        id
        person {
          id
          displayName
          pgaId
        }
        amountCents
        fundingStatus
        payoutEligibilityStatus
        holdReasons
        lineKind
        receivedAt
        payableAt
        paidOutAt
        expiredAt
        fundingBatch {
          id
          status
        }
        releaseBatch {
          id
          status
        }
        splitLines {
          id
        }
        campaign {
          id
          editionYear
          partner {
            id
            name
          }
          program {
            id
            name
          }
        }
      }
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;
